import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Typography, Paper, Skeleton, Alert, Stack, Chip, Divider, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, CircularProgress
} from '@mui/material';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import API_BASE_URL from '../../Config/config';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import { billingApi, formatPaise, FEATURE_META, describeLineUnits } from '../api/billingApi';

const CARD_BG = '#FFFFFF';
const BORDER = '1px solid #E5E7EB';

const STATUS_CONFIG = {
  success: { bg: 'rgba(34,197,94,0.1)', color: '#22C55E', border: 'rgba(34,197,94,0.25)', label: 'Success' },
  pending: { bg: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: 'rgba(245,158,11,0.25)', label: 'Pending' },
  failed: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', border: 'rgba(239,68,68,0.25)', label: 'Failed' },
  refunded: { bg: 'rgba(107,114,128,0.1)', color: '#9CA3AF', border: 'rgba(107,114,128,0.2)', label: 'Refunded' },
};

const PLAN_DISPLAY = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const PLAN_COLORS = {
  starter: '#06B6D4',
  professional: '#6C3BFF',
  enterprise: '#F59E0B',
};

/* Feature-billing order statuses (TSG Nexus feature credits). */
const ORDER_STATUS_CONFIG = {
  PAID: { bg: 'rgba(34,197,94,0.1)', color: '#22C55E', border: 'rgba(34,197,94,0.25)', label: 'Paid' },
  PENDING: { bg: 'rgba(245,158,11,0.1)', color: '#B45309', border: 'rgba(245,158,11,0.25)', label: 'Pending' },
  PAYMENT_INITIATED: { bg: 'rgba(6,182,212,0.12)', color: '#0E7490', border: 'rgba(6,182,212,0.26)', label: 'In progress' },
  FAILED: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', border: 'rgba(239,68,68,0.25)', label: 'Failed' },
  CANCELLED: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', border: 'rgba(239,68,68,0.25)', label: 'Cancelled' },
  EXPIRED: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', border: 'rgba(107,114,128,0.2)', label: 'Expired' },
  REFUNDED: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', border: 'rgba(107,114,128,0.2)', label: 'Refunded' },
  PARTIALLY_REFUNDED: { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', border: 'rgba(107,114,128,0.2)', label: 'Part refunded' },
};

/** "AI Tutor · AI Tutor Credits · 2 → 200 prompts" — the order line in words. */
const describeOrderItem = (item) => {
  const meta = FEATURE_META[item.feature] || {};
  const parts = [meta.label || item.feature, item.productName];
  if (item.technologyTitle || item.technology) parts.push(item.technologyTitle || item.technology);
  parts.push(item.accessType === 'FULL_ACCESS' ? describeLineUnits(item) : `${item.quantity} → ${describeLineUnits(item, meta.units || 'units')}`);
  return parts.join(' · ');
};

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
};

const NextGenPayments = () => {
  const { nextgen_builder } = useNextGenAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');

  /* ── TSG Nexus feature purchases (feature-credit orders) ── */
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [orderDownloadId, setOrderDownloadId] = useState(null);

  const handleDownloadOrderInvoice = async (order) => {
    setDownloadError('');
    setOrderDownloadId(order.id);
    try {
      const data = await billingApi.downloadInvoice(order.id);
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${order.invoiceNumber || order.orderNumber || `order-${order.id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError((err && err.message) || 'Failed to download invoice');
    } finally {
      setOrderDownloadId(null);
    }
  };

  const handleDownloadInvoice = async (payment) => {
    setDownloadError('');
    setDownloadingId(payment.id);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/payments/${payment.id}/invoice`, {
        headers: {
          // API Gateway's REST API only base64-decodes a Lambda proxy
          // response back into real binary when the request's Accept header
          // matches one of the API's configured BinaryMediaTypes exactly
          // (application/pdf is one of them). Without this, the browser's
          // default `Accept: */*` doesn't match, so API Gateway passes the
          // base64 STRING straight through as the body — the download
          // "succeeds" (right filename, ~4/3 the real PDF's size) but every
          // PDF reader rejects it because it isn't actually binary PDF data.
          Accept: 'application/pdf',
          ...(nextgen_builder?.id != null ? { 'x-builder-id': nextgen_builder.id } : {}),
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${payment.razorpay_order_id || payment.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // err.response.data is a Blob here (responseType: 'blob'), so the JSON
      // error message the backend sent has to be read back out of it.
      let message = 'Failed to download invoice';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          message = JSON.parse(text)?.message || message;
        } catch (_) { /* keep default message */ }
      } else {
        message = err.response?.data?.message || message;
      }
      setDownloadError(message);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        // Explicit header as a defense-in-depth backstop alongside the
        // global axios interceptor fix — nextgenAuthMiddleware on the
        // backend 400s this route without a builder identity header.
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/payments`, {
          headers: nextgen_builder?.id != null ? { 'x-builder-id': nextgen_builder.id } : {},
        });
        // Guard against a non-array response ever reaching payments.map()
        // below and crashing the page.
        setPayments(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load payment history');
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, [nextgen_builder?.id]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await billingApi.listOrders();
        setOrders(Array.isArray(data) ? data : (Array.isArray(data?.orders) ? data.orders : []));
      } catch (err) {
        // A builder who has never bought feature credits (or whose session
        // token has expired) should still see the plan payments below —
        // this section degrades quietly instead of failing the page.
        if (!err || err.status !== 401) setOrdersError((err && err.message) || 'Failed to load feature purchases');
      } finally {
        setOrdersLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const paidOrders = orders.filter((o) => o.status === 'PAID');
  const featureSpendPaise = paidOrders.reduce((sum, o) => sum + Number(o.grandTotalPaise || 0), 0);

  return (
    <motion.div {...fadeUp}>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>

        {/* ── Header ── */}
        <Box
          sx={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            mb: 3.5, flexWrap: 'wrap', gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', mb: 0.4 }}>
              Payment History
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
              Feature credit purchases and project plan subscriptions, with invoices
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<ShoppingCartRoundedIcon />}
            onClick={() => navigate('/nextgen-builder/purchase')}
            sx={{
              borderRadius: '10px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6C3BFF, #8B5CF6)',
              '&:hover': { background: 'linear-gradient(135deg, #5020E0, #7C3AED)' },
            }}
          >
            New Subscription
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {downloadError && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDownloadError('')}>{downloadError}</Alert>}

        {/* ── Summary Stats (if data exists) ── */}
        {!loading && (payments.length > 0 || paidOrders.length > 0) && (
          <Box
            sx={{
              display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap',
            }}
          >
            {[
              {
                label: 'Total Payments',
                value: payments.length + paidOrders.length,
                color: '#6C3BFF',
              },
              {
                label: 'Total Invested',
                value: `₹${payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString('en-IN')}`,
                color: '#6C3BFF',
              },
              {
                label: 'Total Saved',
                value: `₹${payments.reduce((sum, p) => sum + parseFloat(p.discount_amount || 0), 0).toLocaleString('en-IN')}`,
                color: '#22C55E',
              },
              ...(paidOrders.length > 0 ? [{
                label: 'Feature Credits',
                value: formatPaise(featureSpendPaise),
                color: '#F59E0B',
              }] : []),
            ].map(({ label, value, color }) => (
              <Paper
                key={label}
                elevation={0}
                sx={{
                  px: 2, py: 1.5, borderRadius: '12px',
                  background: `${color}08`,
                  border: `1px solid ${color}18`,
                }}
              >
                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
                  {label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color, letterSpacing: '-0.01em' }}>
                  {value}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* ── Feature purchases (TSG Nexus feature credits) ── */}
        <Paper elevation={0} sx={{ borderRadius: '16px', background: CARD_BG, border: BORDER, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BoltRoundedIcon sx={{ color: '#9B6FFF', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                Feature purchases
              </Typography>
            </Box>
            <Button
              size="small"
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: '14px !important' }} />}
              onClick={() => navigate('/nextgen-builder/plans')}
              sx={{ color: '#6C3BFF', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: 'rgba(108,59,255,0.06)' } }}
            >
              Buy feature credits
            </Button>
          </Box>
          <Divider />

          {ordersError && <Alert severity="error" sx={{ m: 2.5 }}>{ordersError}</Alert>}

          {ordersLoading ? (
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={1.25}>
                {[1, 2].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: '10px' }} />
                ))}
              </Stack>
            </Box>
          ) : orders.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 600, mb: 0.5 }}>
                No feature purchases yet
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 2 }}>
                Every feature includes a free allowance. Buy credits only when you need more.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<BoltRoundedIcon />}
                onClick={() => navigate('/nextgen-builder/plans')}
                sx={{
                  borderColor: 'rgba(108,59,255,0.3)', color: '#6C3BFF', borderRadius: '10px',
                  fontWeight: 600, textTransform: 'none',
                  '&:hover': { borderColor: '#6C3BFF', bgcolor: 'rgba(108,59,255,0.06)' },
                }}
              >
                View feature credits
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Order', 'Items', 'Subtotal', 'GST', 'Total', 'Status', 'Date', 'Invoice', ''].map((h, i) => (
                      <TableCell key={`${h}-${i}`}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((o) => {
                    const s = ORDER_STATUS_CONFIG[o.status] || ORDER_STATUS_CONFIG.PENDING;
                    return (
                      <TableRow key={o.id} sx={{ '&:hover': { bgcolor: 'rgba(108,59,255,0.04)' } }}>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: '"Plus Jakarta Sans", monospace', color: '#0F172A', fontWeight: 700, fontSize: '0.74rem' }}
                          >
                            {o.orderNumber}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Stack spacing={0.35}>
                            {(o.items || []).map((item) => (
                              <Typography key={item.id} variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.72rem' }}>
                                {describeOrderItem(item)}
                              </Typography>
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.82rem' }}>
                            {formatPaise(o.subtotalPaise)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.82rem' }}>
                            {formatPaise(o.taxPaise)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '0.88rem', letterSpacing: '-0.01em' }}>
                            {formatPaise(o.grandTotalPaise)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={o.status === 'PAID' ? <CheckCircleRoundedIcon sx={{ fontSize: '12px !important' }} /> : undefined}
                            label={s.label}
                            size="small"
                            sx={{
                              bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`,
                              fontWeight: 700, fontSize: '0.7rem', height: 22,
                              '& .MuiChip-icon': { color: `${s.color} !important` },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                            {o.createdAt
                              ? new Date(o.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {o.invoiceNumber ? (
                            <Tooltip title={`Download ${o.invoiceNumber}`}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={orderDownloadId === o.id}
                                  onClick={() => handleDownloadOrderInvoice(o)}
                                  sx={{ color: '#6C3BFF' }}
                                >
                                  {orderDownloadId === o.id
                                    ? <CircularProgress size={16} sx={{ color: '#6C3BFF' }} />
                                    : <DownloadRoundedIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#CBD5E1' }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View order">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/nextgen-builder/orders/${o.id}`)}
                              sx={{ color: '#94A3B8', '&:hover': { color: '#6C3BFF' } }}
                            >
                              <ReceiptLongRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* ── Table ── */}
        <Paper elevation={0} sx={{ borderRadius: '16px', background: CARD_BG, border: BORDER, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentRoundedIcon sx={{ color: '#9B6FFF', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
              Project plan payments
            </Typography>
          </Box>
          <Divider />

          {loading ? (
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={1.25}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={60} sx={{ borderRadius: '10px' }} />
                ))}
              </Stack>
            </Box>
          ) : payments.length === 0 ? (
            <Box sx={{ py: 7, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56, height: 56, borderRadius: '16px', mx: 'auto', mb: 2,
                  background: 'rgba(108,59,255,0.08)',
                  border: '1px solid rgba(108,59,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#9B6FFF',
                }}
              >
                <PaymentRoundedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 600, mb: 0.5 }}>
                No Payments Yet
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 2.5 }}>
                Get started with a subscription plan to unlock all features.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ShoppingCartRoundedIcon />}
                onClick={() => navigate('/nextgen-builder/purchase')}
                sx={{
                  borderColor: 'rgba(108,59,255,0.3)',
                  color: '#6C3BFF',
                  borderRadius: '10px',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#6C3BFF', bgcolor: 'rgba(108,59,255,0.06)' },
                }}
              >
                Browse Plans
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Plan', 'Amount Paid', 'Discount', 'Order ID', 'Status', 'Validity', 'Date', 'Invoice'].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p) => {
                    const s = STATUS_CONFIG[p.payment_status] || STATUS_CONFIG.pending;
                    const planColor = PLAN_COLORS[p.subscription_type] || '#6C3BFF';
                    return (
                      <TableRow key={p.id} sx={{ '&:hover': { bgcolor: 'rgba(108,59,255,0.04)' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 6, height: 6, borderRadius: '50%',
                                bgcolor: planColor, flexShrink: 0,
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '0.85rem' }}>
                              {PLAN_DISPLAY[p.subscription_type] || p.subscription_type || '—'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '0.9rem', letterSpacing: '-0.01em' }}>
                            ₹{parseFloat(p.amount).toLocaleString('en-IN')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: p.discount_amount > 0 ? 700 : 400,
                              color: p.discount_amount > 0 ? '#22C55E' : '#94A3B8',
                              fontSize: '0.85rem',
                            }}
                          >
                            {p.discount_amount > 0 ? `−₹${parseFloat(p.discount_amount).toLocaleString('en-IN')}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: '"Plus Jakarta Sans", monospace', color: '#94A3B8', fontSize: '0.72rem' }}
                          >
                            {p.razorpay_order_id ? `${p.razorpay_order_id.slice(0, 14)}…` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={p.payment_status === 'success' ? <CheckCircleRoundedIcon sx={{ fontSize: '12px !important' }} /> : undefined}
                            label={s.label}
                            size="small"
                            sx={{
                              bgcolor: s.bg,
                              color: s.color,
                              border: `1px solid ${s.border}`,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22,
                              '& .MuiChip-icon': { color: `${s.color} !important` },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                            {p.validity_days ? `${p.validity_days} days` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                            {new Date(p.created_at).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {p.payment_status === 'success' ? (
                            <Tooltip title="Download Invoice">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={downloadingId === p.id}
                                  onClick={() => handleDownloadInvoice(p)}
                                  sx={{ color: '#6C3BFF' }}
                                >
                                  {downloadingId === p.id
                                    ? <CircularProgress size={16} sx={{ color: '#6C3BFF' }} />
                                    : <DownloadRoundedIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#CBD5E1' }}>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </motion.div>
  );
};

export default NextGenPayments;
